// Heavy-lane swarm — the "Working now" block of the Agent-fleet page (H1).
//
// One card per heavy worker slot (the dispatcher runs app_requests on a
// 3-slot lane; see backend/modules/fleet GET /api/fleet/heavy). A busy slot is
// a window into a live Claude Code CLI session: the request, its current
// step (parsed from the worker's ticker line in app_request.log), the last
// steps, running time against how long such jobs usually take, the context
// size and the share of the subscription quota it has used so far. Idle slots
// show what's next in the queue. Below: the queue, and recent results.
// (Requests waiting on a person stay on the Board — they are the requester's
// ball, not the fleet's.)
//
// Copy rules (review 2026-08-27): one headline figure per element, words not
// abbreviations ("5-hour window", "steps", "min"), "worker" not "pod", times
// as on a clock, forecasts as a sentence, "under 1%" not "<1%". Every number
// a person might ask about carries a Hint (Radix tooltip), not a browser title.
//
// All data is read from existing tables — nothing here needs a fleet
// redeploy. The `customers` fork queue (also heavy) is NOT visible yet.
import React, { useState } from "react";
import { Loader2, Square, Radio, Clock, Bot } from "lucide-react";
import { REQUEST_KIND_META, fmtTokens, logStamp } from "../board/requestMeta";
import { cancelMyRequest } from "../../services/appRequests";
import { Tooltip, TooltipTrigger, TooltipContent } from "../ui/tooltip";
import Hint from "./Hint";
import { fmtDur, fmtAgo, fmtWhen, fmtQuota, workerState } from "./format";

const kindLabelOf = (kind) => (REQUEST_KIND_META[kind] || { label: kind }).label;
const titleOf = (r) => r.title || `${kindLabelOf(r.kind)} · ${r.project_slug}`;
const quotaNote = (q) => q.shared_with
  ? `shared with ${q.shared_with} other job${q.shared_with === 1 ? "" : "s"} running at the same time, so the rise was split by overlap time`
  : "this job ran alone on the subscription, so the whole rise is its own";

// Kind chip — label + lucide glyph from the Board's kind registry.
function KindChip({ kind }) {
  const meta = REQUEST_KIND_META[kind] || { label: kind, icon: Bot };
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
      <Icon size={11} /> {meta.label}
    </span>
  );
}

function WorkerDot({ alive, known }) {
  const w = workerState(alive, known);
  return (
    <Hint text={alive ? "The worker running this job reported a heartbeat within the last 10 minutes."
      : known ? "The worker that claimed this job has stopped reporting heartbeats — it may be restarting."
      : "The worker that claimed this job no longer exists (a replaced pod). Nothing is working on it."}>
      <span className="inline-flex items-center gap-1 text-[11px] text-page-muted">
        <span className="inline-block size-2 rounded-full" style={{ background: w.color }} />{w.label}
      </span>
    </Hint>
  );
}

// Effort of a finished job — one short line, the breakdown in a real tooltip.
//   "17 min · 116 steps · quota 2% · 12.2M read"
function EffortCell({ r }) {
  const ctx = (r.tokens_in || 0) + (r.tokens_cache_creation || 0);
  const avgCtx = r.turns ? r.tokens_cache_read / r.turns : null;
  const q = r.quota && r.quota.measured ? r.quota : null;
  const parts = [fmtDur(r.duration_s)];
  if (r.turns) parts.push(`${r.turns} steps`);
  if (q) parts.push(`quota ${fmtQuota(q["5h"])}`);
  if (r.tokens) parts.push(`${fmtTokens(r.tokens)} read`);
  const summary = parts.join(" · ");
  if (!r.tokens && !q) return <span>{summary}<span className="opacity-70"> · no metrics</span></span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="border-b border-dotted border-page-muted cursor-help" data-testid="fleet-heavy-effort">{summary}</span>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" className="max-w-sm text-left leading-snug">
        {q && (
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 tabular-nums mb-1.5 pb-1.5 border-b border-white/20">
            <span>share of the 5-hour window</span><b>{fmtQuota(q["5h"])}</b>
            <span>share of the 7-day window</span><b>{fmtQuota(q["7d"])}</b>
            <span className="col-span-2 opacity-80">Measured from the subscription's utilization while the job ran; {quotaNote(q)}.</span>
          </div>
        )}
        {r.tokens > 0 && (
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 tabular-nums">
            <span>read into context</span><b>{fmtTokens(ctx)}</b>
            <span className="pl-3">fresh input</span><span>{fmtTokens(r.tokens_in || 0)}</span>
            <span className="pl-3">written to cache</span><span>{fmtTokens(r.tokens_cache_creation || 0)}</span>
            <span>re-read from cache</span><b>{fmtTokens(r.tokens_cache_read || 0)}</b>
            {avgCtx != null && <><span className="pl-3">on average per step</span><span>{fmtTokens(avgCtx)} × {r.turns}</span></>}
            <span>written by the model</span><b>{fmtTokens(r.tokens_out || 0)}</b>
            <span className="pt-1 border-t border-white/20 col-span-2">everything read = all of the above · {fmtTokens(r.tokens)}</span>
          </div>
        )}
        <div className="mt-1.5 opacity-80">"Read into context" is the figure the Board shows. "Everything read" includes every cache re-read — that is what the subscription quota feels.</div>
      </TooltipContent>
    </Tooltip>
  );
}

// One busy slot.
function SlotCard({ job, onStop, stopping }) {
  const typ = job.typical;
  const el = job.elapsed_s || 0;
  const kindLabel = kindLabelOf(job.kind);
  // No steps for 3+ minutes: the worker is in a long tool call — or stuck.
  const stale = job.idle_s != null && job.idle_s > 180;
  // Bar = running time against the kind's p90 (how long such jobs usually
  // take — NOT progress; we cannot know how much work is left). Colour is
  // about health, in words:
  //   green — within the usual time
  //   amber — longer than usual, but the worker is active (long jobs are
  //           normal; a live #399 ran 2× its p90 and finished fine)
  //   red   — ONLY past p90 AND silent 3+ min: "may be stuck"
  let color = "#94a3b8", pct = 0, verdict = "";
  let stats = "No finished jobs of this kind yet, so there is no usual duration to compare with.";
  if (typ?.p90_s) {
    pct = Math.min(100, Math.round((el / typ.p90_s) * 100));
    stats = `How long "${kindLabel}" jobs usually take: median ${fmtDur(typ.median_s)}, 9 of 10 finish within ${fmtDur(typ.p90_s)} (from ${typ.n} finished job${typ.n === 1 ? "" : "s"} in the last 30 days). The bar shows running time against that 9-of-10 mark — it is not progress.`;
    if (el > typ.p90_s && stale) { color = "#dc2626"; verdict = `may be stuck — no steps for ${fmtDur(job.idle_s)} · slower than 9 of 10 such jobs`; }
    else if (el > typ.p90_s) { color = "#d97706"; verdict = `slower than 9 of 10 such ${kindLabel} jobs — still working`; }
    else if (el > typ.median_s) { color = "#d97706"; verdict = `longer than usual — 9 of 10 such jobs finish within ${fmtDur(typ.p90_s)}`; }
    else if (stale) { color = "#d97706"; verdict = `no steps for ${fmtDur(job.idle_s)} — a long tool call, or stuck`; }
    else { color = "#16a34a"; }
  } else if (stale) { color = "#d97706"; verdict = `no steps for ${fmtDur(job.idle_s)} — a long tool call, or stuck`; }
  const tick = job.tick;
  const current = tick
    ? (tick.detail || `${tick.tool} call`)
    : (job.tail?.length ? job.tail[job.tail.length - 1].msg : "Claimed — waiting for the first step…");
  return (
    <div className="border border-card-border rounded-[10px] bg-card-bg p-3.5 flex flex-col min-h-[230px] relative"
         data-testid={`fleet-heavy-running-${job.id}`} data-state="busy">
      <Hint text="Cancels this request. The worker running it is stopped within about 15 seconds; whatever it has already changed stays as it is." className="absolute top-2.5 right-2.5 border-0">
        <button
          type="button"
          onClick={() => onStop(job)}
          disabled={stopping === job.id}
          className="inline-flex items-center gap-1 h-6 px-2 rounded-[5px] text-[11px] text-red-700 border border-card-border bg-card-bg hover:bg-red-50 cursor-pointer disabled:opacity-50"
          data-testid={`fleet-heavy-stop-${job.id}`}
        >
          {stopping === job.id ? <Loader2 size={11} className="animate-spin" /> : <Square size={10} />} Stop
        </button>
      </Hint>
      <div className="flex items-center gap-2 pr-16">
        <KindChip kind={job.kind} />
        <span className="text-[11px] text-page-muted">#{job.id}</span>
      </div>
      <div className="font-medium text-page-title text-sm mt-2 leading-snug" title={job.title || ""}>{titleOf(job)}</div>
      <div className="text-xs text-page-muted mt-0.5">
        {job.project_slug}{job.requester ? ` · by ${job.requester.split("@")[0]}` : ""} · started {fmtAgo(job.claimed_at)}
      </div>
      <div className="mt-2.5 text-xs text-page-title flex items-center gap-1.5" data-testid="fleet-heavy-current">
        <Loader2 size={11} className={`shrink-0 ${stale ? "text-amber-600" : "text-blue-500 animate-spin"}`} />
        <span className="truncate" title={current}>{current}</span>
        {tick && <span className="shrink-0 text-page-muted">· step {tick.calls} ({tick.tool})</span>}
      </div>
      <div className="mt-2.5">
        <div className="flex justify-between text-[11px] text-page-muted tabular-nums">
          <span>running {fmtDur(job.elapsed_s, { withSeconds: true })} · last step {job.idle_s != null && job.idle_s < 5 ? "just now" : `${fmtDur(job.idle_s)} ago`}</span>
          <Hint text={stats} align="end">{typ?.median_s ? `usually about ${fmtDur(typ.median_s)}` : "no history for this kind"}</Hint>
        </div>
        <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden mt-1" data-testid="fleet-heavy-pace">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </div>
        {verdict && <div className="text-[11px] mt-0.5" style={{ color }} data-testid="fleet-heavy-verdict">{verdict}</div>}
      </div>
      {job.tail?.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-dashed border-card-border font-mono text-[10.5px] leading-relaxed text-page-muted">
          {job.tail.slice(-3).map((e, i, arr) => (
            <div key={i} className={`truncate ${i === arr.length - 1 ? "text-page-title" : ""}`} title={e.msg}>
              {logStamp(e.at)} {e.msg}
            </div>
          ))}
        </div>
      )}
      <div className="mt-auto pt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-page-muted tabular-nums">
        {tick?.tokens != null && (
          <Hint text="Tokens read into this session's context so far (fresh input plus what was written to cache). Every step re-reads them from cache, so the job's final 'everything read' figure ends up many times larger.">
            <span>context <b className="text-page-title font-medium">{fmtTokens(tick.tokens)}</b></span>
          </Hint>
        )}
        {job.quota?.measured && (
          <Hint text={`Share of the subscription used since this job started: ${fmtQuota(job.quota["5h"])} of the 5-hour window, ${fmtQuota(job.quota["7d"])} of the 7-day window. Measured from the subscription's utilization; ${quotaNote(job.quota)}.`}>
            <span data-testid="fleet-heavy-quota">quota <b className="text-page-title font-medium">{fmtQuota(job.quota["5h"])}</b> of the 5-hour window so far</span>
          </Hint>
        )}
        {tick?.calls != null && (
          <Hint text="Steps so far — every tool call the agent has made (reading files, running commands, editing). The Board's Activity log shows them one by one.">
            <span>{tick.calls} steps</span>
          </Hint>
        )}
        <WorkerDot alive={job.pod_alive} known={job.pod_known} />
      </div>
    </div>
  );
}

function IdleSlot({ next, index }) {
  return (
    <div className="border border-dashed border-card-border rounded-[10px] bg-page-bg p-3.5 min-h-[230px] flex flex-col items-center justify-center text-center text-xs text-page-muted gap-1"
         data-testid="fleet-heavy-slot-idle" data-state="idle">
      <div>worker {index + 1} — idle</div>
      {next && <div className="text-[11px]">next: #{next.id} {titleOf(next)} (waiting since {fmtWhen(next.created_at)})</div>}
    </div>
  );
}

export default function HeavySwarm({ data, error, onChanged }) {
  const [stopping, setStopping] = useState(null);
  const [stopError, setStopError] = useState(null);

  if (error) {
    return <div className="mb-4 px-3 py-2 rounded-[5px] bg-red-50 text-red-700 text-sm" data-testid="fleet-heavy-error">{error}</div>;
  }
  if (!data) {
    return <div className="flex items-center gap-2 text-page-muted text-sm py-4"><Loader2 size={14} className="animate-spin" /> Loading the heavy lane…</div>;
  }

  const running = data.running || [];
  const queued = data.queued || [];
  const recent = data.recent || [];
  const max = Math.max(data.slots?.max || 3, running.length);
  const d = data.last_24h || {};
  const q24 = d.quota || {};
  const alivePods = (data.pods || []).filter((p) => p.alive).length;

  const onStop = async (job) => {
    const ok = window.confirm(`Stop #${job.id} "${titleOf(job)}"?\n\nThe request is cancelled and the worker running it is stopped within about 15 seconds.`);
    if (!ok) return;
    setStopping(job.id); setStopError(null);
    try { await cancelMyRequest(job.id); onChanged?.(); }
    catch (e) { setStopError(e?.message || `Failed to stop #${job.id}`); }
    finally { setStopping(null); }
  };

  const Tile = ({ k, v, s, testid, hint }) => (
    <div className="border border-card-border rounded-[8px] bg-card-bg px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-page-muted"><Hint text={hint} align="start">{k}</Hint></div>
      <div className="text-lg font-semibold text-page-title tabular-nums leading-tight">{v}{s && <span className="text-[11px] font-normal text-page-muted ml-1.5">{s}</span>}</div>
    </div>
  );

  return (
    <div className="mb-6 max-w-[980px]" data-testid="fleet-heavy">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-medium text-page-title flex items-center gap-2">
          Working now — heavy lane
          <Hint text="Refreshed every 5 seconds while this tab is visible. The heavy lane runs Board requests (new projects, changes, apps) as Claude Code CLI sessions on the fleet's subscription." className="border-0">
            <span className="inline-flex items-center gap-1 px-2 py-0 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
              <Radio size={10} className="animate-pulse" /> live · {fmtAgo(data.generated_at)}
            </span>
          </Hint>
        </h2>
        <span className="text-[11px] text-page-muted">
          Board requests · Claude Code CLI{data.pods?.length ? ` · ${alivePods} of ${data.pods.length} worker${data.pods.length === 1 ? "" : "s"} online` : ""}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="fleet-heavy-tiles">
        <Tile k="Busy workers" v={`${data.slots?.busy ?? running.length} of ${data.slots?.max ?? max}`} s={data.slots?.orphaned ? `${data.slots.orphaned} on gone workers` : ""} testid="fleet-heavy-tile-slots"
              hint={`Heavy-lane worker slots in use: ${data.slots?.per_pod ?? 3} per worker pod, times the pods online. "on gone workers" means a request was claimed by a pod that no longer reports — nothing is working on it.`} />
        <Tile k="Waiting" v={queued.length} s={data.todo_count ? `${data.todo_count} parked on Board` : ""} testid="fleet-heavy-tile-queued"
              hint={'Requests waiting for a free worker — taken oldest-first, per organisation. "Parked on Board" are To-do cards: nobody has queued them yet, so workers do not see them.'} />
        <Tile k="Finished today" v={d.done || 0} s={`done · ${d.failed || 0} failed${d.cancelled ? ` · ${d.cancelled} cancelled` : ""}`} testid="fleet-heavy-tile-done"
              hint={'Requests that finished in the last 24 hours, by outcome.'} />
        <Tile k="Quota used today" v={q24["5h"] != null ? fmtQuota(q24["5h"]) : "—"} s={q24["5h"] != null ? `of a 5-hour window · week: ${fmtQuota(q24["7d"])}` : "history is still collecting"} testid="fleet-heavy-tile-quota"
              hint={'How much of the subscription the whole fleet burned in the last 24 hours. Headline: the summed rises of the 5-hour windows (as if it were one window). Small: the rise of the 7-day window. Counts every lane and every worker on this subscription, not only the heavy lane.'} />
        <Tile k="Tokens read today" v={fmtTokens(d.tokens || 0)} s={`${d.turns || 0} steps`} testid="fleet-heavy-tile-processed"
              hint={`Everything the model read in the last 24 hours, cache re-reads included — the volume the subscription quota feels. Steps = tool calls and replies. Of this, ${fmtTokens(d.context_tokens || 0)} were tokens read into context for the first time (the figure the Board shows per request).`} />
      </div>

      {stopError && <div className="mb-2 px-3 py-2 rounded-[5px] bg-red-50 text-red-700 text-sm">{stopError}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="fleet-heavy-slots">
        {Array.from({ length: max }).map((_, i) => (
          running[i]
            ? <SlotCard key={running[i].id} job={running[i]} onStop={onStop} stopping={stopping} />
            : <IdleSlot key={`idle-${i}`} index={i} next={queued[i - running.length] || null} />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="fleet-heavy-queue">
        <span className="text-page-muted uppercase text-[10px] tracking-wide mr-1">Queue</span>
        <Hint text="Requests a worker will pick up next, oldest first within each organisation." className="border-0">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${queued.length ? "border-amber-300 bg-amber-50 text-amber-900" : "border-card-border bg-card-bg text-page-title"}`}>
            <Clock size={11} /> waiting for a worker <b className="tabular-nums">{queued.length}</b>
          </span>
        </Hint>
        <Hint text="To-do cards on the Board: written down, not yet handed to the fleet. They start when someone moves them to the queue." className="border-0">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-card-border bg-card-bg text-page-title">
            parked on the Board <b className="tabular-nums">{data.todo_count || 0}</b>
          </span>
        </Hint>
        {queued.slice(0, 3).map((q) => (
          <span key={q.id} className="text-page-muted">#{q.id} {titleOf(q)}</span>
        ))}
      </div>

      {recent.length > 0 && (
        <div className="mt-4" data-testid="fleet-heavy-recent">
          <div className="text-[10px] uppercase tracking-wide text-page-muted mb-1.5">Recent results</div>
          <div className="border border-card-border rounded-[8px] bg-card-bg overflow-hidden">
            <table className="w-full text-xs border-collapse">
              <tbody>
                {recent.slice(0, 8).map((r) => (
                  <tr key={r.id} className="border-t border-card-border first:border-t-0" data-testid={`fleet-heavy-recent-${r.id}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap text-page-muted tabular-nums w-[110px]">{fmtWhen(r.finished_at)}</td>
                    <td className="px-3 py-1.5 w-[70px]">
                      <span className={`font-medium ${r.status === "done" ? "text-emerald-700" : r.status === "failed" ? "text-red-700" : "text-page-muted"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-1.5 truncate max-w-[420px] text-page-title"><KindChip kind={r.kind} /> <span className="text-page-muted">#{r.id}</span> {titleOf(r)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-page-muted tabular-nums">
                      <EffortCell r={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-page-muted mt-1">
            quota = share of the 5-hour window the job used · read = everything the model read, cache included · steps = tool calls and replies. Hover a row for the full breakdown.
          </div>
        </div>
      )}
    </div>
  );
}
